import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { NeoCard, NeoBadge } from "@/components/NeoCard";
import { SlotButton } from "@/components/SlotButton";
import {
  buildTimeSlots,
  DEFAULT_START_MIN,
  DEFAULT_END_MIN,
  EXTEND_STEP_MIN,
  SLOT_MIN_BOUND,
  SLOT_MAX_BOUND,
  slotId,
  toISODate,
  formatDayLabel,
} from "@/lib/availability";
import { createMeeting, getApiErrorMessage } from "@/lib/api";
import { useTypewriterPlaceholder } from "@/hooks/useTypewriterPlaceholder";
import { toast } from "sonner";
import { Copy, Link2, ArrowRight, CalendarDays, Sparkles } from "lucide-react";

const TITLE_SUGGESTIONS = [
  "Q2 roadmap review",
  "Dinner",
  "Coffee chat",
  "Birthday party",
  "1:1 with Sarah",
  "Movie night",
  "Sprint planning",
  "Late dinner",
];

export default function Landing() {
  const [title, setTitle] = useState("");
  const [dates, setDates] = useState([]); // Date[]
  const [preferred, setPreferred] = useState(new Set()); // Set<slotId>
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [timezone] = useState("America/New_York");
  const [error, setError] = useState("");
  const [startMin, setStartMin] = useState(DEFAULT_START_MIN);
  const [endMin, setEndMin] = useState(DEFAULT_END_MIN);
  const [fineGrained, setFineGrained] = useState(false);
  const stepMin = fineGrained ? 15 : 30;
  const timeSlots = useMemo(
    () => buildTimeSlots(startMin, endMin, stepMin),
    [startMin, endMin, stepMin]
  );
  const canExtendEarlier = startMin > SLOT_MIN_BOUND;
  const canExtendLater = endMin < SLOT_MAX_BOUND;
  const extendEarlier = () =>
    setStartMin((m) => Math.max(SLOT_MIN_BOUND, m - EXTEND_STEP_MIN));
  const extendLater = () =>
    setEndMin((m) => Math.min(SLOT_MAX_BOUND, m + EXTEND_STEP_MIN));
  const isRangeDefault =
    startMin === DEFAULT_START_MIN &&
    endMin === DEFAULT_END_MIN &&
    !fineGrained;
  const [resetGlowing, setResetGlowing] = useState(false);
  const handleReset = () => {
    if (resetGlowing) {
      setPreferred(new Set());
      setResetGlowing(false);
      return;
    }
    let nextStart = DEFAULT_START_MIN;
    let nextEnd = DEFAULT_END_MIN;
    let nextFine = false;
    for (const sid of preferred) {
      const time = sid.split("|")[1];
      if (!time) continue;
      const [h, m] = time.split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) continue;
      const minutes = h * 60 + m;
      if (minutes < nextStart) nextStart = minutes;
      if (minutes > nextEnd) nextEnd = minutes;
      if (m % 30 !== 0) nextFine = true;
    }
    setStartMin(Math.max(SLOT_MIN_BOUND, nextStart));
    setEndMin(Math.min(SLOT_MAX_BOUND, nextEnd));
    setFineGrained(nextFine);
    setResetGlowing(true);
  };
  useEffect(() => {
    if (!resetGlowing) return undefined;
    const id = setTimeout(() => setResetGlowing(false), 3000);
    return () => clearTimeout(id);
  }, [resetGlowing]);
  useEffect(() => {
    if (resetGlowing && preferred.size === 0) setResetGlowing(false);
  }, [resetGlowing, preferred]);
  const titleInputRef = useRef(null);
  const titlePlaceholder = useTypewriterPlaceholder(TITLE_SUGGESTIONS, {
    inputRef: titleInputRef,
  });

  const days = useMemo(() => (dates || []).map(toISODate).sort(), [dates]);
  const allowedPreferred = useMemo(
    () => new Set(days.flatMap((day) => timeSlots.map((time) => slotId(day, time)))),
    [days, timeSlots]
  );

  useEffect(() => {
    setPreferred((prev) => {
      const daySet = new Set(days);
      const next = new Set(
        [...prev].filter((sid) => daySet.has(sid.split("|")[0]))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [days]);

  const validate = () => {
    if (days.length === 0) return "Pick at least one day.";
    if (timeSlots.length === 0) return "Add at least one time slot.";
    if ([...preferred].some((sid) => !allowedPreferred.has(sid))) {
      return "Preferred slots must be one of the selected days and times.";
    }
    return "";
  };

  const togglePref = (sid) => {
    setPreferred((prev) => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  };

  const handleCreate = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return toast.error(validationError);
    }
    setCreating(true);
    setError("");
    try {
      const meeting = await createMeeting({
        title: title.trim() || "Untitled meeting",
        days,
        timeSlots,
        preferredSlots: [...preferred].filter((sid) => allowedPreferred.has(sid)),
        timezone,
      });
      const participantUrl = `${window.location.origin}${meeting.participantPath}`;
      const adminPath =
        meeting.adminPath ||
        `/admin/${meeting.id}?adminToken=${encodeURIComponent(meeting.adminToken || "")}`;
      const adminUrl = `${window.location.origin}${adminPath}`;
      setCreated({ id: meeting.id, participantUrl, adminUrl });
      toast.success("Meeting created");
    } catch (e) {
      const message = getApiErrorMessage(e, "Could not create meeting");
      setError(message);
      toast.error(message);
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const copy = async (url, message) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(message);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (created) {
    return (
      <NeoCard className="max-w-2xl w-full" data-testid="created-card">
        <div className="flex items-center gap-2 mb-4">
          <NeoBadge variant="lime">
            <Sparkles className="w-3 h-3" /> LIVE
          </NeoBadge>
          <span className="text-xs font-mono text-black/60">api</span>
        </div>
        <h2 className="neo-heading text-3xl sm:text-4xl mb-2">
          Meeting is live.
        </h2>
        <p className="font-mono text-sm text-black/70 mb-6">
          Share the participant link publicly. Keep the admin link private.
        </p>

        <div className="space-y-4 mb-6">
          <div>
            <div className="mb-1.5 font-bold text-xs uppercase tracking-wider">
              Participant link
            </div>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex-1 flex items-center gap-2 border-[2px] border-black bg-[#f7f1dd] rounded-lg px-3 py-2.5 font-mono text-[13px] overflow-hidden">
                <Link2 className="w-4 h-4 shrink-0" />
                <span className="truncate" data-testid="share-link">
                  {created.participantUrl}
                </span>
              </div>
              <Button
                onClick={() => copy(created.participantUrl, "Participant link copied")}
                data-testid="copy-link-btn"
                className="bg-[#ffd60a] hover:bg-[#ffea5c] text-black border-[2px] border-black rounded-lg shadow-[3px_3px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[4px_4px_0_0_#000] active:translate-y-[1px] active:shadow-[1px_1px_0_0_#000] font-bold"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
          </div>

          <div>
            <div className="mb-1.5 font-bold text-xs uppercase tracking-wider text-[#b00020]">
              ADMIN link
            </div>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex-1 flex items-center gap-2 border-[2px] border-black bg-[#ff5e78] rounded-lg px-3 py-2.5 font-mono text-[13px] text-black overflow-hidden shadow-[3px_3px_0_0_#000]">
                <Link2 className="w-4 h-4 shrink-0" />
                <span className="truncate" data-testid="admin-link">
                  {created.adminUrl}
                </span>
              </div>
              <Button
                variant="outline"
                onClick={() => copy(created.adminUrl, "Admin link copied")}
                className="bg-white text-black hover:bg-[#ffe1e7] border-[2px] border-black rounded-lg shadow-[3px_3px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[4px_4px_0_0_#000] font-bold"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <p className="mt-2 font-mono text-[11px] text-black/65">
              This link controls closing, reopening, public results, and finalizing.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => (window.location.href = `/m/${created.id}`)}
            data-testid="open-participant-btn"
            className="flex-1 bg-black text-white hover:bg-black/90 border-[2px] border-black rounded-lg shadow-[3px_3px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[4px_4px_0_0_#000] font-bold"
          >
            Open as participant <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            onClick={() => (window.location.href = created.adminUrl)}
            data-testid="open-results-btn"
            className="flex-1 bg-white text-black hover:bg-[#f7f1dd] border-[2px] border-black rounded-lg shadow-[3px_3px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[4px_4px_0_0_#000] font-bold"
          >
            Open admin dashboard
          </Button>
        </div>
      </NeoCard>
    );
  }

  return (
    <NeoCard className="max-w-3xl w-full" data-testid="landing-card">
      <div className="flex items-center justify-between mb-4">
        <NeoBadge variant="yellow">
          <CalendarDays className="w-3 h-3" /> NEW MEETING
        </NeoBadge>
        <span className="font-mono text-[11px] text-black/50">Secure</span>
      </div>

      <h1 className="neo-heading text-4xl sm:text-5xl leading-[0.95] mb-2">
        When should we meet?
      </h1>
      <p className="font-mono text-base text-black/70 mb-6">
        Pick days, mark your preferred slots, send the link.
      </p>

      <label className="block font-bold text-xs uppercase tracking-wider mb-1.5">
        Title
      </label>
      <Input
        ref={titleInputRef}
        data-testid="meeting-title-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={titlePlaceholder}
        className="border-[2px] border-black rounded-lg font-mono text-sm h-11 shadow-[3px_3px_0_0_#000] focus-visible:ring-0 focus-visible:shadow-[4px_4px_0_0_#000] mb-6"
      />

      <div className="mb-6 border-[2px] border-black rounded-xl bg-[#fff9ed] p-3 font-mono text-xs text-black/70">
        Timezone: <span className="font-bold text-black">{timezone}</span>
      </div>

      <label className="block font-bold text-xs uppercase tracking-wider mb-2 text-center">
        Possible days
      </label>
      <div className="flex justify-center mb-8">
        <div className="inline-block border-[2px] border-black rounded-xl bg-[#fff9ed] shadow-[4px_4px_0_0_#000] p-2">
          <Calendar
            mode="multiple"
            selected={dates}
            onSelect={(d) => setDates(d || [])}
            disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
            data-testid="day-calendar"
            className="text-base"
          />
        </div>
      </div>

      {days.length > 0 && (
        <>
          <label className="block font-bold text-xs uppercase tracking-wider mb-1">
            Mark preferred slots{" "}
            <span className="text-black/50 normal-case tracking-normal">
              (optional - click any slot to star it)
            </span>
          </label>
          <p className="font-mono text-xs text-black/60 mb-4">
            Preferred slots get priority when availability ties.
          </p>
          <div className="space-y-4 mb-6">
            {days.map((day) => (
              <div
                key={day}
                className="border-[2px] border-black rounded-xl bg-[#fff9ed] p-4"
              >
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="neo-heading text-lg">{formatDayLabel(day)}</h3>
                    <button
                      type="button"
                      onClick={extendEarlier}
                      disabled={!canExtendEarlier}
                      data-testid="extend-earlier-btn"
                      className="bg-[#a8e1ff] hover:bg-[#c2eaff] text-black border-[2px] border-black rounded-md shadow-[2px_2px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_0_#000] active:translate-y-[1px] active:shadow-[1px_1px_0_0_#000] font-bold uppercase tracking-wider text-[10px] px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[2px_2px_0_0_#000]"
                    >
                      + Early
                    </button>
                    <button
                      type="button"
                      onClick={extendLater}
                      disabled={!canExtendLater}
                      data-testid="extend-later-btn"
                      className="bg-[#1e1b4b] hover:bg-[#312e81] text-white border-[2px] border-black rounded-md shadow-[2px_2px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_0_#000] active:translate-y-[1px] active:shadow-[1px_1px_0_0_#000] font-bold uppercase tracking-wider text-[10px] px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[2px_2px_0_0_#000]"
                    >
                      + Late
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFineGrained((v) => !v)}
                      data-testid="toggle-fine-grained-btn"
                      aria-pressed={fineGrained}
                      className={`${
                        fineGrained
                          ? "bg-[#ffd60a] hover:bg-[#ffea5c]"
                          : "bg-white hover:bg-[#f7f1dd]"
                      } text-black border-[2px] border-black rounded-md shadow-[2px_2px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_0_#000] active:translate-y-[1px] active:shadow-[1px_1px_0_0_#000] font-bold uppercase tracking-wider text-[10px] px-2 py-1`}
                    >
                      :15
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={isRangeDefault && preferred.size === 0}
                      data-testid="reset-range-btn"
                      aria-pressed={resetGlowing}
                      className={`${
                        resetGlowing
                          ? "bg-[#ffd60a] hover:bg-[#ffea5c] reset-glow"
                          : "bg-white hover:bg-[#f7f1dd] shadow-[2px_2px_0_0_#000] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_0_#000] active:translate-y-[1px] active:shadow-[1px_1px_0_0_#000]"
                      } text-black border-[2px] border-black rounded-md font-bold uppercase tracking-wider text-[10px] px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[2px_2px_0_0_#000]`}
                    >
                      {resetGlowing ? "Reset all?" : "Reset"}
                    </button>
                    <span className="font-mono text-[11px] text-black/50">
                      {[...preferred].filter((s) => s.startsWith(day + "|"))
                        .length}{" "}
                      preferred
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {timeSlots.map((t) => {
                    const sid = slotId(day, t);
                    return (
                      <SlotButton
                        key={sid}
                        time={t}
                        selected={preferred.has(sid)}
                        mode="prefer"
                        onClick={() => togglePref(sid)}
                        testId={`pref-slot-${sid}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Button
        onClick={handleCreate}
        disabled={creating}
        data-testid="create-meeting-btn"
        className="w-full h-12 bg-black text-white hover:bg-black/90 border-[2px] border-black rounded-lg shadow-[4px_4px_0_0_#ffd60a] hover:-translate-y-[2px] hover:shadow-[6px_6px_0_0_#ffd60a] active:translate-y-[1px] active:shadow-[2px_2px_0_0_#ffd60a] font-bold uppercase tracking-wider"
      >
        {creating ? "Creating..." : "Create meeting"}
      </Button>
      {error && (
        <p className="mt-3 font-mono text-sm text-[#b00020]" role="alert">
          {error}
        </p>
      )}
    </NeoCard>
  );
}
